import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { FiShield, FiAlertTriangle, FiActivity, FiLock, FiUnlock, FiCheckCircle, FiDownload, FiRefreshCw } from 'react-icons/fi';
import { buildApiUrl } from '../../config/api';
import { fetchJson, formatFetchError } from '../../utils/fetchJson';

const Security = () => {
  const [securityLogs, setSecurityLogs] = useState([]);
  const [loginAttempts, setLoginAttempts] = useState([]);

  const [blockedIPs, setBlockedIPs] = useState([]);
  const [stats, setStats] = useState({
    activeSessions: 0,
    failedLogins: 0,
    blockedIPs: 0,
    securityEvents: 0
  });
  const [securitySettings, setSecuritySettings] = useState({
    twoFactorAuth: false,
    sessionTimeout: 24,
    maxLoginAttempts: 5,
    lockoutDuration: 30,
    ipWhitelist: '',
    allowedFileTypes: 'jpg,jpeg,png,pdf',
    maxFileSize: 5
  });
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');



  const fetchSecurityData = useCallback(async () => {


    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required');
      }

      const authHeaders = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const { response: logsResponse, data: logsData } = await fetchJson(buildApiUrl('/api/admin/security/logs'), {
        headers: authHeaders
      });
      if (logsResponse.ok && logsData.success) {
        setSecurityLogs(logsData.data);
      }

      const { response: attemptsResponse, data: attemptsData } = await fetchJson(buildApiUrl('/api/admin/security/login-attempts'), {
        headers: authHeaders
      });
      if (attemptsResponse.ok && attemptsData.success) {
        setLoginAttempts(attemptsData.data);
      }

      const { response: ipsResponse, data: ipsData } = await fetchJson(buildApiUrl('/api/admin/security/blocked-ips'), {
        headers: authHeaders
      });
      if (ipsResponse.ok && ipsData.success) {
        setBlockedIPs(ipsData.data);
      }



      const { response: statsResponse, data: statsData } = await fetchJson(buildApiUrl('/api/admin/security/stats'), {
        headers: authHeaders
      });
      if (statsResponse.ok && statsData.success) {
        setStats(statsData.data);
      }

      const { response: settingsResponse, data: settingsData } = await fetchJson(buildApiUrl('/api/admin/security/settings'), {
        headers: authHeaders
      });
      if (settingsResponse.ok && settingsData.success) {
        const newSettings = {
          twoFactorAuth: settingsData.data.twoFactorAuth || false,
          sessionTimeout: settingsData.data.sessionTimeout || 24,
          maxLoginAttempts: settingsData.data.maxLoginAttempts || 5,
          lockoutDuration: settingsData.data.lockoutDuration || 30,
          ipWhitelist: (settingsData.data.ipWhitelist || []).join(', '),
          allowedFileTypes: settingsData.data.allowedFileTypes || 'jpg,jpeg,png,pdf',
          maxFileSize: settingsData.data.maxFileSize || 5
        };
        setSecuritySettings(newSettings);
      }

    } catch (error) {
      console.error('Error fetching security data:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSecurityData();
  }, [activeTab, fetchSecurityData]);

  const handleUnblockIP = async (ipId) => {
    try {
      const token = localStorage.getItem('token');
      const { response, data } = await fetchJson(buildApiUrl(`/api/admin/security/unblock-ip/${ipId}`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok && data.success) {
        setBlockedIPs(blockedIPs.filter(ip => ip.id !== ipId));
      } else {
        alert(data?.message || 'Failed to unblock IP');
      }
    } catch (error) {
      console.error('Error unblocking IP:', error);
      alert(formatFetchError(error, 'Failed to unblock IP. Please try again.'));
    }
  };

  const handleUpdateSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const { response, data } = await fetchJson(buildApiUrl('/api/admin/security/settings'), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(securitySettings)
      });

      if (response.ok && data.success) {
        alert('Security settings updated successfully');
      } else {
        alert(data?.message || 'Failed to update security settings');
      }
    } catch (error) {
      console.error('Error updating settings:', error);
      alert(formatFetchError(error, 'Failed to update security settings.'));
    }
  };

  const handleExportLogs = () => {
    try {
      if (securityLogs.length === 0) {
        alert('No logs to export');
        return;
      }

      // Define CSV headers
      const headers = ['Date', 'Type', 'Severity', 'User', 'IP Address', 'Location', 'Details'];

      // Convert logs to CSV rows
      const csvContent = securityLogs.map(log => {
        const date = new Date(log.createdAt).toLocaleString().replace(/,/g, ''); // Remove commas to prevent CSV issues
        const type = log.type || '';
        const severity = log.severity || '';
        const user = log.user ? `${log.user.firstName} ${log.user.lastName} (${log.user.email})` : 'Unknown';
        const ip = log.ip || '';
        const location = log.location || '';
        const details = (log.details || '').replace(/,/g, ';'); // Escape commas in details

        return [date, type, severity, user, ip, location, details].join(',');
      });

      // Combine headers and rows
      const csv = [headers.join(','), ...csvContent].join('\n');

      // Create blob and download link
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `security_logs_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error exporting logs:', error);
      alert('Failed to export logs');
    }
  };

  const getSeverityBadge = (severity) => {
    const badges = {
      info: 'bg-blue-100 text-blue-800',
      warning: 'bg-yellow-100 text-yellow-800',
      danger: 'bg-red-100 text-red-800'
    };
    return badges[severity] || 'bg-gray-100 text-gray-800';
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: 'bg-red-100 text-red-800',
      inactive: 'bg-green-100 text-green-800',
      failed: 'bg-yellow-100 text-yellow-800',
      blocked: 'bg-red-100 text-red-800'
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Security Center</h1>
            <p className="text-sm text-gray-500">Monitor and manage system security</p>
          </div>
          <div className="mt-4 md:mt-0 flex space-x-3">
            <button
              onClick={fetchSecurityData}
              disabled={isLoading}
              className={`px-4 py-2 bg-gray-600 text-white rounded-md text-sm font-medium flex items-center ${isLoading ? 'opacity-70 cursor-not-allowed' : 'hover:bg-gray-700'}`}
            >
              <FiRefreshCw className={`mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              {isLoading ? 'Refreshing...' : 'Refresh'}
            </button>
            <button
              onClick={handleExportLogs}
              className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 flex items-center"
            >
              <FiDownload className="mr-2" />
              Export Logs
            </button>
          </div>
        </div>

        {/* Security Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 rounded-full">
                <FiShield className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Active Sessions</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.activeSessions}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-red-100 rounded-full">
                <FiAlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Failed Logins</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.failedLogins}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-yellow-100 rounded-full">
                <FiLock className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Blocked IPs</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.blockedIPs}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 rounded-full">
                <FiActivity className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Security Events</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.securityEvents}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              {['overview', 'logs', 'login-attempts', 'blocked-ips', 'settings'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-3 px-6 border-b-2 font-medium text-sm ${activeTab === tab
                    ? 'border-red-500 text-red-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                  {tab.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'overview' && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Security Overview</h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3">Recent Security Events</h4>
                    <div className="space-y-2">
                      {securityLogs.slice(0, 3).map(log => (
                        <div key={log.id} className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">{log.type.replace('_', ' ')}</span>
                          <span className={`px-2 py-1 rounded-full text-xs ${getSeverityBadge(log.severity)}`}>
                            {log.severity}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3">System Status</h4>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Firewall Status</span>
                        <span className="text-green-600 flex items-center">
                          <FiCheckCircle className="mr-1" /> Active
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">SSL Certificate</span>
                        <span className="text-green-600 flex items-center">
                          <FiCheckCircle className="mr-1" /> Valid
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Last Security Scan</span>
                        <span className="text-gray-500">2 hours ago</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'logs' && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Security Logs</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Event Type</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP Address</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Severity</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {securityLogs.map(log => (
                        <tr key={log._id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-900 capitalize">{log.type.replace('_', ' ')}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {log.user ? `${log.user.firstName} ${log.user.lastName}` : 'Unknown User'}
                              </div>
                              <div className="text-sm text-gray-500">{log.email}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{log.ip}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{log.location}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(log.createdAt).toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs rounded-full ${getSeverityBadge(log.severity)}`}>
                              {log.severity}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'login-attempts' && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Failed Login Attempts</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP Address</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Attempts</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Attempt</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {loginAttempts.map(attempt => (
                        <tr key={attempt._id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{attempt.email}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{attempt.ip}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{attempt.attempts}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(attempt.lastAttempt).toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(attempt.status)}`}>
                              {attempt.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'blocked-ips' && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Blocked IP Addresses</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP Address</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Blocked At</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Attempts</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {blockedIPs.map(ip => (
                        <tr key={ip._id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{ip.ip}</td>
                          <td className="px-6 py-4 text-sm text-gray-500">{ip.reason}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(ip.blockedAt).toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{ip.attempts}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(ip.status)}`}>
                              {ip.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button
                              onClick={() => handleUnblockIP(ip._id)}
                              className="text-green-600 hover:text-green-900 flex items-center"
                            >
                              <FiUnlock className="mr-1" />
                              Unblock
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'settings' && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Security Settings</h3>
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Two-Factor Authentication
                      </label>
                      <select
                        value={securitySettings.twoFactorAuth}
                        onChange={(e) => setSecuritySettings({ ...securitySettings, twoFactorAuth: e.target.value === 'true' })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500"
                      >
                        <option value="false">Disabled</option>
                        <option value="true">Enabled</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Session Timeout (hours)
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="72"
                        value={securitySettings.sessionTimeout}
                        onChange={(e) => setSecuritySettings({ ...securitySettings, sessionTimeout: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Max Login Attempts
                      </label>
                      <input
                        type="number"
                        min="3"
                        max="10"
                        value={securitySettings.maxLoginAttempts}
                        onChange={(e) => setSecuritySettings({ ...securitySettings, maxLoginAttempts: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Lockout Duration (minutes)
                      </label>
                      <input
                        type="number"
                        min="5"
                        max="1440"
                        value={securitySettings.lockoutDuration}
                        onChange={(e) => setSecuritySettings({ ...securitySettings, lockoutDuration: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      IP Whitelist (comma-separated)
                    </label>
                    <textarea
                      value={securitySettings.ipWhitelist}
                      onChange={(e) => setSecuritySettings({ ...securitySettings, ipWhitelist: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500"
                      placeholder="192.168.1.1, 10.0.0.1"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Allowed File Types
                      </label>
                      <input
                        type="text"
                        value={securitySettings.allowedFileTypes}
                        onChange={(e) => setSecuritySettings({ ...securitySettings, allowedFileTypes: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Max File Size (MB)
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="100"
                        value={securitySettings.maxFileSize}
                        onChange={(e) => setSecuritySettings({ ...securitySettings, maxFileSize: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <button
                      onClick={handleUpdateSettings}
                      className="px-6 py-2 bg-red-600 text-white rounded-md font-medium hover:bg-red-700"
                    >
                      Save Settings
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default Security;
